"use client";

import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaMobile, FaCode, FaRocket, FaTools, FaCloud, FaShieldAlt } from "react-icons/fa";
import config from "../../../config/services.json";
import HeroSection from "../../../components/common/HeroSection";

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export default function MobileAppsPage() {
  const { developmentProcess } = config;
  return (
    <main className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <HeroSection
          title="Mobile App Development"
          description="Building innovative mobile experiences that engage users and drive business growth"
          showPattern={true}
        />
      </section>

      {/* Services Overview */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="space-y-6"
            >
              <h2 className="text-3xl font-bold">Modern Mobile Solutions for Your Business</h2>
              <p className="text-lg text-gray-600">
                We create custom mobile applications that are fast, secure, and scalable. 
                Our development team uses the latest technologies to deliver solutions that meet your 
                business needs and exceed user expectations.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center mt-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700">Native iOS and Android development</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center mt-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700">Cross-platform solutions for wider reach</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center mt-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700">Intuitive user interfaces and experiences</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center mt-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700">Robust backend integration and APIs</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="relative"
            >
              <div className="relative h-[400px] md:h-[600px] w-full">
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] md:w-[280px] h-[400px] md:h-[560px] 
                  bg-black rounded-[30px] md:rounded-[40px] p-3 md:p-4 shadow-2xl transform rotate-12">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 md:w-24 h-4 md:h-6 bg-black rounded-b-2xl"></div>
                  <div className="w-full h-full bg-gradient-to-br from-indigo-600 to-purple-600 rounded-[24px] md:rounded-[32px]"></div>
                </div>
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] md:w-[280px] h-[400px] md:h-[560px] 
                  bg-black rounded-[30px] md:rounded-[40px] p-3 md:p-4 shadow-2xl transform -rotate-12">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 md:w-24 h-4 md:h-6 bg-black rounded-b-2xl"></div>
                  <div className="w-full h-full bg-gradient-to-br from-purple-600 to-indigo-600 rounded-[24px] md:rounded-[32px]"></div>
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 w-24 md:w-32 h-24 md:h-32 bg-indigo-600 rounded-full opacity-20"></div>
              <div className="absolute -top-6 -right-6 w-24 md:w-32 h-24 md:h-32 bg-purple-600 rounded-full opacity-20"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold mb-6">Our Mobile App Services</h2>
            <p className="text-lg text-gray-600">
              We offer comprehensive mobile app development services to bring your vision to life.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <FaMobile className="w-6 h-6" />,
                title: "iOS Development",
                description: "Native iOS applications built with Swift for exceptional performance and user experience."
              },
              {
                icon: <FaCode className="w-6 h-6" />,
                title: "Android Development",
                description: "Robust Android apps developed with Kotlin to reach the widest audience possible."
              },
              {
                icon: <FaRocket className="w-6 h-6" />,
                title: "Cross-Platform Apps",
                description: "Cost-effective solutions using React Native or Flutter for multi-platform deployment."
              },
              {
                icon: <FaCloud className="w-6 h-6" />,
                title: "Cloud Integration",
                description: "Seamless integration with cloud services for scalable and reliable applications."
              },
              {
                icon: <FaShieldAlt className="w-6 h-6" />,
                title: "App Security",
                description: "Implementation of robust security measures to protect user data and privacy."
              },
              {
                icon: <FaTools className="w-6 h-6" />,
                title: "Maintenance & Support",
                description: "Continuous updates and support to keep your app running smoothly and efficiently."
              }
            ].map((service, index) => (
              <motion.div
                key={service.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 
                  text-white flex items-center justify-center mb-6">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold mb-4">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold mb-6">Our Development Process</h2>
            <p className="text-lg text-gray-600">
              We follow a structured approach to deliver high-quality mobile solutions that meet your business objectives.
            </p>
          </motion.div>

          <div className="relative max-w-5xl mx-auto h-auto md:h-[700px] perspective-[2000px]">
            { developmentProcess.map((process, index) => (
              <motion.div
                key={process.step}
                className="block mb-6 md:mb-4 md:absolute md:inset-x-0"
                style={{
                  top: `${index * 140}px`,
                  transformStyle: "preserve-3d"
                }}
                initial={{ opacity: 0, rotateX: 45, y: 100 }}
                whileInView={{
                  opacity: 1,
                  rotateX: 0,
                  y: 0
                }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.8,
                  delay: index * 0.2,
                  ease: "easeOut"
                }}
                whileHover={{
                  scale: 1.02,
                  rotateX: -5,
                  transition: { duration: 0.3 }
                }}
              >
                <div className={`bg-gradient-to-r ${process.color} p-6 md:p-8 rounded-3xl shadow-2xl 
                  text-white relative overflow-hidden group`}>
                  <div className="absolute -right-8 -top-8 w-24 md:w-32 h-24 md:h-32 bg-white/10 rounded-full blur-2xl 
                    group-hover:scale-150 transition-transform duration-500" />
                  
                  <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">
                    <div className="flex-shrink-0 w-12 h-12 md:w-16 md:h-16 bg-white/20 rounded-2xl flex items-center 
                      justify-center text-2xl md:text-3xl backdrop-blur-sm">
                      {process.icon}
                    </div>
                    <div className="space-y-2 md:space-y-4">
                      <div className="flex items-center gap-2 md:gap-3">
                        <span className="text-3xl md:text-5xl font-bold opacity-50">{process.step}</span>
                        <h3 className="text-xl md:text-2xl font-bold">{process.title}</h3>
                      </div>
                      <p className="text-base md:text-lg text-white/80">{process.description}</p>
                    </div>
                  </div>

                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0"
                    initial={{ x: "-100%" }}
                    whileHover={{ x: "100%" }}
                    transition={{ duration: 1 }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="text-3xl md:text-4xl font-bold mb-6"
            >
              Ready to Start Your Mobile App Project?
            </motion.h2>
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: 0.1 }}
              className="text-lg text-indigo-100 mb-10"
            >
              Let's discuss how we can help you achieve your business goals with a custom mobile solution.
            </motion.p>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: 0.2 }}
            >
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 rounded-full bg-white text-indigo-600 
                  hover:shadow-lg hover:shadow-indigo-700/20 transition-all duration-300 text-lg font-semibold"
              >
                Get a Free Consultation
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}